import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";
import Header from "../components/Header";
import RatingModal from "../components/RatingModal";
import { api, formatApiErrorDetail } from "../lib/api";

function fmt(iso) { if(!iso) return ""; const d=new Date(iso); return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`; }

const STATUS_CLASS = {
  submitted: "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100",
  reviewing: "bg-sky-50 text-sky-700 ring-1 ring-sky-100",
  interviewing: "bg-fuchsia-50 text-fuchsia-700 ring-1 ring-fuchsia-100",
  shortlisted: "bg-teal-50 text-teal-700 ring-1 ring-teal-100",
  hired: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100",
  rejected: "bg-rose-50 text-rose-700 ring-1 ring-rose-100",
  withdrawn: "bg-slate-100 text-slate-600 ring-1 ring-slate-200",
};

export default function MySubmissions() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratedIds, setRatedIds] = useState({});
  const [ratingTarget, setRatingTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/submissions/mine");
      setItems(data);
      const terminals = data.filter((s) => ["hired", "rejected", "withdrawn"].includes(s.status));
      const rated = {};
      await Promise.all(terminals.map(async (s) => {
        try {
          const r = await api.get(`/submissions/${s.id}/ratings`, { skipPaywallRedirect: true });
          const mine = r.data.find((x) => x.direction === "agency_to_employer");
          if (mine) rated[s.id] = mine.overall;
        } catch (err) { /* per-submission rating fetch: intentionally swallowed — a missing rating just means "not rated yet" */
          if (err.response?.status !== 404) console.warn("submission rating fetch failed:", err); }
      }));
      setRatedIds(rated);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const withdraw = async (id) => {
    if (!window.confirm(t("submissions.confirm_withdraw"))) return;
    try {
      await api.post(`/submissions/${id}/status`, { new_status: "withdrawn" });
      toast.success(t("submissions.withdrawn_toast"));
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    }
  };

  const canWithdraw = (s) => ["submitted", "reviewing", "interviewing", "shortlisted"].includes(s);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="my-submissions-page">
      <Toaster richColors position="top-right" />
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">{t("submissions.mine_title")}</h1>
        <p className="mt-2 text-sm text-slate-600">{t("submissions.mine_subtitle")}</p>

        {loading && <div className="mt-6 text-center text-slate-500">{t("common.loading")}</div>}

        {!loading && items.length === 0 && (
          <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center shadow-sm" data-testid="my-submissions-empty">
            <p className="text-sm text-slate-600">{t("submissions.mine_empty")}</p>
            <Link to="/marketplace" className="mt-4 inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700">
              {t("submissions.browse_marketplace_cta")}
            </Link>
          </div>
        )}

        {!loading && items.length > 0 && (
          <ul className="mt-6 grid gap-3" data-testid="my-submissions-list">
            {items.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`my-sub-${s.id}`}>
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2">
                    <Link to={`/missions/${s.mission_id}`} className="text-base font-semibold text-slate-900 hover:text-indigo-700">
                      {s.mission?.title || "—"}
                    </Link>
                    <span className={"inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider " + (STATUS_CLASS[s.status] || STATUS_CLASS.withdrawn)} data-testid={`my-sub-status-${s.id}`}>
                      {t(`submissions.status_${s.status}`)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {s.consultant.display_name} · {s.proposed_tjm}/day · {t("submissions.sent")} {fmt(s.submitted_at)}
                    {s.mission?.poster_name && ` · ${s.mission.poster_name}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Link to={`/missions/${s.mission_id}`} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                    {t("common.edit")}
                  </Link>
                  {canWithdraw(s.status) && (
                    <button onClick={() => withdraw(s.id)} className="rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50" data-testid={`my-sub-withdraw-${s.id}`}>
                      {t("submissions.withdraw")}
                    </button>
                  )}
                  {["hired", "rejected", "withdrawn"].includes(s.status) && (
                    ratedIds[s.id] ? (
                      <span className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800" data-testid={`my-sub-rated-${s.id}`}>★ {ratedIds[s.id]} / 5</span>
                    ) : (
                      <button
                        onClick={() => setRatingTarget({ id: s.id, ratee_name: s.mission?.poster_name || s.mission?.title || t("rating.the_employer") })}
                        className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100"
                        data-testid={`my-sub-rate-${s.id}`}
                      >
                        {t("rating.rate_button")}
                      </button>
                    )
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        {ratingTarget && (
          <RatingModal
            submissionId={ratingTarget.id}
            direction="agency_to_employer"
            ratee_name={ratingTarget.ratee_name}
            onClose={() => setRatingTarget(null)}
            onSubmitted={() => load()}
          />
        )}
      </main>
    </div>
  );
}
